import type React from 'react';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { InputField, type InputFieldProps } from '@components/InputField';
import { cva } from 'class-variance-authority';

import sharedClasses from '../utils/shared-classes';

const textAreaClasses = cva(
  [
    'appearance-none',
    'block',
    'border-info-300',
    'border',
    'focus:outline-primary-500',
    'px-3',
    'py-2',
    'rounded-md',
    'shadow-xs',
    'text-sm',
    'text-info-700',
    'w-full',
  ],
  {
    variants: {
      isIncomplete: { true: 'field-incomplete' },
      isReadOnly: { true: [sharedClasses.disabled, 'bg-info-100'] },
      // 18px = vertical padding (py-2) + borders; a manual drag writes an inline height, which
      // lifts the max clamp so the box can be stretched past the auto range
      autoGrow: {
        true: [
          'field-sizing-content',
          'min-h-[calc(3lh+18px)]',
          'max-h-[calc(20lh+18px)]',
          '[&[style*=height]]:max-h-none',
          'resize-y',
          'overflow-y-auto',
        ],
      },
    },
  },
);

export interface InputTextareaProps
  extends Omit<InputFieldProps, 'children' | 'size'>, React.InputHTMLAttributes<HTMLTextAreaElement> {
  autoGrow?: boolean;
  className?: string;
  errors?: string;
  id?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  labelText?: string;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  value?: string;
}
const AUTO_MAX_LINES = 20;
const VERTICAL_CHROME_PX = 18;

// Manual resize is only sticky past the auto max — within it, a value change hands the height back to auto
const releaseManualHeightWithinAutoRange = (el: HTMLTextAreaElement) => {
  if (!el.style.height) return;
  const autoMax = AUTO_MAX_LINES * parseFloat(getComputedStyle(el).lineHeight) + VERTICAL_CHROME_PX;
  if (parseFloat(el.style.height) <= autoMax) {
    el.style.height = '';
  }
};

const InputTextarea = forwardRef<HTMLTextAreaElement, InputTextareaProps>(function InputTextArea(props, ref) {
  const {
    autoGrow,
    cdrId,
    cdrName,
    className,
    descriptionText,
    errors,
    id,
    isDisabled,
    isReadOnly,
    isRequired,
    helperText,
    labelIcon,
    labelText,
    mrcId,
    name,
    shortName,
    value,
    isIncomplete,
    hideErrors,
    dataTestId,
    ...rest
  } = props;

  const disabled = isDisabled || isReadOnly;
  const _id = id ?? name;

  const innerRef = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement);

  useEffect(() => {
    if (autoGrow && innerRef.current) {
      releaseManualHeightWithinAutoRange(innerRef.current);
    }
  }, [autoGrow, value]);

  return (
    <InputField
      cdrId={cdrId}
      cdrName={cdrName}
      className={className}
      descriptionText={descriptionText}
      errors={errors}
      id={_id}
      isRequired={isRequired}
      helperText={helperText}
      labelIcon={labelIcon}
      labelText={labelText}
      mrcId={mrcId}
      name={name}
      shortName={shortName}
      isIncomplete={isIncomplete}
      hideErrors={hideErrors}
    >
      <textarea
        {...rest}
        data-testid={dataTestId ?? name}
        className={textAreaClasses({ isIncomplete, isReadOnly, autoGrow })}
        id={_id}
        name={name}
        required={isRequired}
        value={value ?? ''}
        rows={3}
        disabled={disabled}
        ref={innerRef}
      />
    </InputField>
  );
});

export default InputTextarea;
